import {crossEnv} from '@sbvr/lbe-environment';
import gql from '@/core/setup/api';
import {logout, netLogin, persistIdentity, retrieveIdentity} from './auth';

type UserToken = {
  id: string;
  token: string;
  redirectOnLoginUrl: string;
  organization: {
    name: string;
    type: string;
    tenantId: string;
  };
};

export async function loginRequest({email, password}: { email: string; password: string }): Promise<UserToken> {
  const mutation = `mutation (
    $email: String
    $password: String
    ) {
      user: authenticateUser (
        email: $email
        password: $password
        ) {
          id
          originalId
          token
          redirectOnLoginUrl
          name
          organization {
            name
            type
            tenantId
            country
            state
            locations {
              id,
              originalId
            }
            games {
              preset {
                originalId
              }
            }
          }
        }
      }`;
  const {user} = await gql.request(mutation, {
    email,
    password,
  }, {retry: 3}) as { user: UserToken };
  try {
    await persistIdentity({token: user.token, tenantId: user.organization?.tenantId});
    await netLogin(email, password);
  } catch (e) {
    logout();
    throw e;
  }
  return user;
};

export async function SSOLogin(redirect): Promise<boolean> {
  const {token} = await retrieveIdentity();
  if (token) {
    window.location.href = `https://${crossEnv.API_URL}${redirect}?token=${token}`;
  }
  return Boolean(token);
};

export async function passwordResetRequest(email: string): Promise<boolean> {
  const mutation = `mutation (
    $email: String
    ) {
      success: sendResetPasswordEmail (email: $email)
    }`;
  const {success} = await gql.request(mutation, {
    email,
  }) as { success: boolean };
  return success;
};

export async function passwordChangeMutation(payload: {
  email: string;
  token: string;
  password: string;
  passwordConfirmation: string;
}): Promise<boolean> {
  const mutation = `mutation (
      $email: String,
      $token: String,
      $password: String,
      $passwordConfirmation: String
    ) {
      success: resetPassword(
        email: $email,
        token: $token,
        password: $password,
        passwordConfirmation: $passwordConfirmation
      )
    }`;
  const {success} = await gql.request(mutation, payload) as { success: boolean };
  return success;
};

export async function signUpMutation(payload: {
  businessType: string | null;
  organizationType: string;
  organizationName: string;
  organizationWebsite: string;
  organizationReferrer: string;
  organizationCountry: string;
  organizationState: string;
  organizationCity: string;
  organizationAddress: string;
  organizationAddress2: string;
  organizationPostalCode: string;
  userFirstName: string;
  userLastName: string;
  userPosition: string;
  contactPhone: string;
  contactEmail: string;
}): Promise<boolean> {
  const mutation = `mutation (
      $businessType: String
      $organizationType: String!
      $organizationName: String!
      $organizationWebsite: String
      $organizationReferrer: String
      $organizationCountry: String!
      $organizationState: String
      $organizationCity: String
      $organizationAddress: String
      $organizationAddress2: String
      $organizationPostalCode: String
      $organizationVatNumber: String
      $userFirstName: String!
      $userLastName: String!
      $userPosition: String
      $contactPhone: String
      $contactEmail: String!
    ) {
      success: registerOrganization(
        businessType: $businessType
        organizationType: $organizationType
        organizationName: $organizationName
        organizationWebsite: $organizationWebsite
        organizationReferrer: $organizationReferrer
        organizationCountry: $organizationCountry
        organizationState: $organizationState
        organizationCity: $organizationCity
        organizationAddress: $organizationAddress
        organizationAddress2: $organizationAddress2
        organizationPostalCode: $organizationPostalCode
        organizationVatNumber: $organizationVatNumber
        userFirstName: $userFirstName
        userLastName: $userLastName
        userPosition: $userPosition
        contactPhone: $contactPhone
        contactEmail: $contactEmail
      )
    }`;
  const {success} = await gql.request(mutation, payload) as { success: boolean };
  return success;
};
